import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChefHat,
  LayoutGrid,
  ListOrdered,
  Moon,
} from "lucide-react";
import { useState } from "react";

import { Button, Card, CardHeader, Money, StatusPill } from "@/components/ui";
import {
  closeBusinessDay,
  getCurrentBusinessDay,
  listChecks,
} from "@/lib/restaurant";
import { getSession } from "@/lib/session";

import { FloorMapScreen } from "./FloorMapScreen";
import { KdsScreen } from "./KdsScreen";

interface Props {
  onOpenCheck: (checkId: string) => void;
}

export function RestaurantScreen({ onOpenCheck }: Props) {
  const queryClient = useQueryClient();
  const session = getSession();
  const branchId = session?.branches?.[0]?.id ?? "";

  const [activeTab, setActiveTab] = useState<"floor" | "checks" | "kds" | "day">("floor");
  const [closeNotes, setCloseNotes] = useState("");
  const [showCloseModal, setShowCloseModal] = useState(false);

  // Comandas abiertas
  const checksQuery = useQuery({
    queryKey: ["restaurant", "checks", branchId],
    queryFn: () => listChecks({ branch_id: branchId, statuses: ["open", "billed"] }),
    enabled: Boolean(branchId) && activeTab === "checks",
    refetchInterval: 10_000,
  });

  // Día operativo
  const businessDayQuery = useQuery({
    queryKey: ["restaurant", "business-day", branchId],
    queryFn: () => getCurrentBusinessDay(branchId),
    enabled: Boolean(branchId) && activeTab === "day",
  });

  const closeDayMutation = useMutation({
    mutationFn: () =>
      closeBusinessDay({
        branch_id: branchId,
        notes: closeNotes || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant"] });
      setShowCloseModal(false);
      setCloseNotes("");
    },
  });

  const checks = checksQuery.data?.items ?? [];
  const businessDay = businessDayQuery.data;

  return (
    <div className="bg-surface text-carbon min-h-[calc(100vh-4rem)] space-y-6 p-4 sm:p-6">
      {/* Navegación por pestañas del vertical */}
      <div className="border-line flex flex-wrap items-center justify-between gap-4 border-b pb-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveTab("floor")}
            className={`flex items-center gap-2 rounded-msr px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === "floor"
                ? "bg-electric text-on-electric shadow-sm"
                : "border-line bg-snow text-carbon hover:bg-line/50 border"
            }`}
          >
            <LayoutGrid className="size-4" />
            <span>Plano de Mesas</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("checks")}
            className={`flex items-center gap-2 rounded-msr px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === "checks"
                ? "bg-electric text-on-electric shadow-sm"
                : "border-line bg-snow text-carbon hover:bg-line/50 border"
            }`}
          >
            <ListOrdered className="size-4" />
            <span>Comandas Activas</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("kds")}
            className={`flex items-center gap-2 rounded-msr px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === "kds"
                ? "bg-electric text-on-electric shadow-sm"
                : "border-line bg-snow text-carbon hover:bg-line/50 border"
            }`}
          >
            <ChefHat className="size-4" />
            <span>KDS Cocina</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("day")}
            className={`flex items-center gap-2 rounded-msr px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === "day"
                ? "bg-electric text-on-electric shadow-sm"
                : "border-line bg-snow text-carbon hover:bg-line/50 border"
            }`}
          >
            <Moon className="size-4" />
            <span>Día Operativo</span>
          </button>
        </div>
      </div>

      {/* Contenido según pestaña */}
      {activeTab === "floor" && <FloorMapScreen onOpenCheck={onOpenCheck} />}

      {activeTab === "kds" && <KdsScreen />}

      {activeTab === "checks" && (
        <div className="space-y-4">
          <h3 className="text-carbon text-base font-semibold">
            Todas las cuentas abiertas en el salón ({checks.length})
          </h3>

          {checks.length === 0 ? (
            <Card className="text-muted p-8 text-center">
              No hay comandas activas en este momento.
            </Card>
          ) : (
            <div className="divide-line border-line bg-snow divide-y rounded-xl border">
              {checks.map((chk) => (
                <div
                  key={chk.id}
                  className="hover:bg-surface/70 flex cursor-pointer items-center justify-between p-4 transition-colors"
                  onClick={() => onOpenCheck(chk.id)}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-carbon font-bold">
                        {chk.label || `Comanda #${chk.series}-${chk.folio}`}
                      </span>
                      <StatusPill tone={chk.status === "open" ? "info" : "warning"}>
                        {chk.status === "open" ? "Abierta" : "Cuenta pedida"}
                      </StatusPill>
                    </div>
                    <p className="text-muted mt-1 text-xs">
                      {chk.guests} personas | Abierta a las{" "}
                      {new Date(chk.opened_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="text-carbon font-mono text-lg font-bold">
                      <Money cents={chk.total_cents} />
                    </span>
                    <Button size="sm" onClick={() => onOpenCheck(chk.id)}>
                      Abrir
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "day" && (
        <div className="max-w-xl space-y-5">
          <Card className="space-y-4 p-6">
            <CardHeader
              title="Día Operativo del Restaurante"
              description="Cierre nocturno tras el horario de corte (cutoff)"
            />

            {businessDay ? (
              <div className="space-y-3">
                <div className="border-line bg-surface space-y-2 rounded-msr border p-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted">Fecha operativa en curso:</span>
                    <span className="text-carbon font-mono font-bold">{businessDay.business_date}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted">Hora de corte (cutoff):</span>
                    <span className="text-carbon font-mono">{businessDay.cutoff_hour}:00 hrs</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted">Estado:</span>
                    <StatusPill tone={businessDay.status === "open" ? "ok" : "neutral"}>
                      {businessDay.status === "open" ? "Abierto" : "Cerrado"}
                    </StatusPill>
                  </div>
                </div>

                {businessDay.status === "open" && (
                  <Button
                    variant="danger"
                    className="w-full"
                    size="lg"
                    onClick={() => setShowCloseModal(true)}
                    icon={<Moon className="size-4" />}
                  >
                    Efectuar Corte y Cierre del Día
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-muted text-sm">Cargando información del día operativo...</p>
            )}
          </Card>
        </div>
      )}

      {/* Modal Cierre de Día */}
      {showCloseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-sm space-y-4 p-6 shadow-2xl">
            <CardHeader
              title="Corte del Día Operativo"
              description="¿Deseas cerrar el día operativo de hoy? Todas las comandas deben estar liquidadas."
            />
            <div>
              <label className="text-muted text-xs font-medium">Notas de cierre (opcional)</label>
              <textarea
                placeholder="Observaciones de caja o cocina..."
                value={closeNotes}
                onChange={(e) => setCloseNotes(e.target.value)}
                className="border-line bg-surface text-carbon focus:border-electric mt-1 h-20 w-full rounded border p-2 text-xs focus:outline-none"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowCloseModal(false)}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={() => closeDayMutation.mutate()}
                disabled={closeDayMutation.isPending}
              >
                Confirmar Cierre
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
